import { Box, Fade, useTheme } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useResizeObserver } from "usehooks-ts";
import { WaveForm, WaveSurfer } from "wavesurfer-react";
import { default as WaveSurferRef } from "wavesurfer.js";
import { updateWaveformPeaksForDownloadedTrack } from "../../../api/files/audio";
import { useAddStyleToShadowRoot } from "../../../hooks/audioPlayerHooks/useAddStyleToShadowRoot";
import { useGetCurrentSeekTime } from "../../../hooks/audioPlayerHooks/useGetCurrentSeekTime";
import { useMoveToSeekTime } from "../../../hooks/audioPlayerHooks/useMoveToSeekTime";
import { useSetupOnClick } from "../../../hooks/audioPlayerHooks/UseSetupOnClick";
import { DEFAULT_TIME } from "../../../hooks/audioPlayerHooks/waveformHooks";
import {
  convertWaveformDurationToReadableTime,
  toggleWaveFormColors,
} from "../../../hooks/audioPlayerHooks/waveformUtils";
import { Text } from "../../core-ui/components/Text/Text";
import {
  TextColor,
  TextStyleVariant,
} from "../../core-ui/components/Text/TextUtils";
import { Size } from "../SessionScheduleGuide/SessionScheduleGuide";
import { useHandleWaveSurferMount } from "../../../hooks/audioPlayerHooks/handleWSMount";
import { FilenameContainer } from "./TrackPlayer.styles";
import { useMediaQueryBreakpoint } from "../../../hooks/useMediaQuery";

export interface FeaturedTrackPlayerProp {
  id: number;
  source: string;
  isPlayingOnFooter: boolean;
  isSameProject: boolean;
  showTime?: boolean;
  fileVersionId?: number;
  waveformPeaks?: string;
  duration?: number;
  isSnippet?: boolean;
  generateWaveform?: boolean;
  useGeneratedMP3?: boolean;
  fileName?: string;
}

export const FeaturedTrackPlayer = ({
  id,
  source,
  isPlayingOnFooter,
  isSameProject,
  showTime = true,
  fileVersionId,
  waveformPeaks = "",
  duration: propDuration,
  generateWaveform = false,
  isSnippet,
  useGeneratedMP3,
  fileName,
}: FeaturedTrackPlayerProp) => {
  const currentTrack = useRef<WaveSurferRef | null>(null);
  const { palette } = useTheme();
  const waveformId = `waveform-${id}`;
  const [width, setWidth] = useState(0); // This holds the container width;
  const containerRef = useRef<HTMLDivElement>(null);
  const [readableDuration, setReadableDuration] =
    useState<string>(DEFAULT_TIME);
  const currentSeekTime = useGetCurrentSeekTime(isSameProject);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    toggleWaveFormColors(currentTrack.current, isPlayingOnFooter);
  }, [isPlayingOnFooter]);

  const { isDesktop } = useMediaQueryBreakpoint();

  const onResize = useCallback(
    (size: Size) => {
      setTimeout(() => {
        setWidth(size.width || 0);
      }, 0);
    },
    [setWidth],
  );
  useResizeObserver({
    ref: containerRef,
    box: "border-box",
    onResize,
  });

  useAddStyleToShadowRoot(
    waveformId,
    width ? `${width}px` : undefined,
    waveformId,
  );

  useEffect(() => {
    // Cleanup function to be executed when the component unmounts or dependencies change
    return () => {
      if (currentTrack.current) {
        currentTrack.current.unAll();
        currentTrack.current.destroy();
        currentTrack.current = null;
      }
      if (window && id && window.surferidze) {
        delete window.surferidze[id];
      }
    };
  }, [source, id]);

  useSetupOnClick(currentTrack, !isPlayingOnFooter);
  useMoveToSeekTime(currentTrack, isSameProject);

  const handleOnReady = useCallback(
    (duration: number) => {
      setLoaded(true);
      currentTrack.current?.setVolume(0);
      setReadableDuration(convertWaveformDurationToReadableTime(duration));
      if (
        fileVersionId &&
        !waveformPeaks &&
        !propDuration &&
        generateWaveform
      ) {
        const peaks = currentTrack.current?.exportPeaks({
          channels: 2,
          maxLength: 1024,
        });
        if (!peaks) return;
        void updateWaveformPeaksForDownloadedTrack({
          fileVersionId: fileVersionId,
          waveformPeaks: peaks[0].join(","),
          isSnippet: isSnippet,
          useGeneratedMP3: useGeneratedMP3,
          duration: duration,
        });
      }
    },
    [
      fileVersionId,
      generateWaveform,
      isSnippet,
      useGeneratedMP3,
      waveformPeaks,
    ],
  );

  const handleWSMount = useHandleWaveSurferMount({
    currentTrack,
    fileVersionId: fileVersionId,
    source,
    onReady: handleOnReady,
    waveformPeaks,
    duration: propDuration,
  });

  return (
    <Fade in={loaded}>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "8px",
          position: "relative",
        }}
        ref={containerRef}
      >
        {showTime && (
          <Text color={TextColor.TERTIARY} variant={TextStyleVariant.P1}>
            {currentSeekTime?.current}
          </Text>
        )}
        <Box sx={{ flex: "1" }}>
          {fileName && isDesktop && (
            <FilenameContainer>
              <Text variant={TextStyleVariant.CAPTION}>{fileName} </Text>
            </FilenameContainer>
          )}
          <WaveSurfer
            plugins={[]}
            onMount={handleWSMount}
            container={`#${waveformId}`}
            waveColor={
              isPlayingOnFooter
                ? palette.standardColor.Gray[400]
                : palette.standardColor.Gray[200]
            }
            progressColor={
              isPlayingOnFooter
                ? palette.standardColor.BoomyOrange[400]
                : palette.standardColor.BoomyOrange[200]
            }
            height={50}
            cursorColor={palette.text.primary}
            peaks={
              waveformPeaks
                ? [
                    waveformPeaks.split(",").map(Number),
                    waveformPeaks.split(",").map(Number),
                  ]
                : undefined
            }
            duration={propDuration ? propDuration : undefined}
          >
            <WaveForm id={waveformId} />
          </WaveSurfer>
        </Box>
        {showTime && (
          <Box sx={{ textAlign: "right" }}>
            <Text color={TextColor.TERTIARY} variant={TextStyleVariant.P1}>
              {readableDuration}
            </Text>
          </Box>
        )}{" "}
      </Box>
    </Fade>
  );
};
